import React, { useEffect, useRef, useState, useMemo } from "react";
import * as PR from "../../../../prime-react/index";
import Layout from "../../../../layouts/Layout";
import "./NewsCategories.scss";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createNewsCategory, getNewsCategories, updateNewsCategory, deleteNewsCategory } from "../../../../services/api";
import { ErrorMessage as ErrorMessageContext , useToast } from "../../../../context/ToastContext";
import { decodeHtml, dompurifyText, getPlainText, textSanitize } from "../../../../utils/reuse";

const catNewsCreate = 'cat_news_delete'

const NewsCategories = () => {
  const { showToast, clearError } = useToast();
  const menuRight = useRef(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newsCategory, setNewsCategory] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const dispatch = useDispatch();
  const sessionData = useSelector((state) => state.auth.sessionData);
  const headers = useMemo(() => {
    return {
      sessionid: sessionData.sessionId,
    };
  }, [sessionData.sessionId]);

  useEffect(() => {
    fetchCategories();
     // eslint-disable-next-line
  }, [headers, dispatch]);

  const fetchCategories = () => {
    setLoading(true);
    getNewsCategories(headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response.data;
        const result = responseData?.length > 0 ? responseData : [];
        setNewsCategory(result);
      } else {
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
      setLoading(false);
    }, true, true);
  };

  const items = [
    {
      label: "Edit Details",
      icon: "pi pi-pencil",
      command: () => openEditDialog(selectedCategory),
    },
    {
      label: "Delete Category",
      icon: "pi pi-trash",
      command: () => handleDeleteNewsCategory()
    },
  ];
  const handleDeleteNewsCategory = () => {
    if(selectedCategory?.newsUpdatesCount) {
      handleDeleteCategory();
    } else {
      setShowDeleteDialog(true);
    }
  }

  const handleDeleteCategory = () => {
    if(selectedCategory?.newsUpdatesCount>0){
      showToast("warn", "Categories with news attached to them can't be deleted");
      return;
    }
    setLoading(true);
    deleteNewsCategory(selectedCategory.id,headers,dispatch,(response) =>{
      if (response.result === "SUCCESS") {
        showToast("success", "Category deleted successfully");
        fetchCategories();
        setShowDeleteDialog(false);
      } else {
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
      setLoading(false);
    })
  }

  const validationSchema = Yup.object({
    name: Yup.string().required("Category name is required").max(50, "Category name must be 50 characters or less"),
    description: Yup.string().test("valid-text", "Enter valid text", (value) =>  !(getPlainText(value) === 0 && value)).required("Category description is required").max(100, "Category description must be 100 characters or less"),
  });

  const handleCreateCategory = (values, setSubmitting) => {
    if(values?.description){
      values.description = textSanitize(values.description);
    }
    clearError(catNewsCreate)
    if (isEdit && selectedCategory) {
      updateNewsCategory(
        { id: selectedCategory.id, ...values },
        headers,
        dispatch,
        (response) => {
          if (response.result === "SUCCESS") {
            showToast("success", "Category updated successfully");
            fetchCategories();
          } else {
            const error = response.error;
            showToast(error.severity, error?.errorMsg || error?.summary, catNewsCreate);
          }
          setSubmitting(false);
          setVisible(false);
          setIsEdit(false);
          setSelectedCategory(null);
        }
      );
    } else {
      createNewsCategory(values, headers, dispatch, (response) => {
        if (response.result === "SUCCESS") {
          showToast("success", "Category created successfully");
          fetchCategories();
        } else {
          const error = response.error;
          showToast(error.severity, error?.errorMsg || error?.summary, catNewsCreate);
        }
        setSubmitting(false);
        setVisible(false);
      });
    }
  };

  const openEditDialog = (category) => {
    setSelectedCategory(category);
    setIsEdit(true);
    setVisible(true);
  };
  const footerContent = (
    <div>
      <ErrorMessageContext id={catNewsCreate} />
      <PR.Button
        label="Cancel"
        onClick={() => {
          setVisible(false);
          setIsEdit(false);
          setSelectedCategory(null);
          clearError(catNewsCreate)
        }}
        outlined
        className="logout-btns"
      />
      <PR.Button
        label={isEdit ? "Update Category" : "Create Category"}
        type="submit"
        form="categoryForm"
        outlined
        autoFocus
        className="logout-btns click-btn"
      />
    </div>
  );

  return (
    <>
      <Layout backBtn={"news"} backBtnLabel="News">
        <section className="all-alerts-section news-categories-section mt-5">
          <div className="m-0 flex align-items-center justify-content-between">
            <h1>News categories</h1>
            <PR.Button
              label="Create New Category"
              icon="pi pi-plus"
              className="create-button"
              onClick={() => setVisible(true)}
            />
          </div>
          {loading ?
            <div className="loading new-updates-loading"> <i className="pi pi-spin pi-spinner"></i> </div>
            : <div className="alerts-tab categories-list">
              <div className="grid">
                {Array.isArray(newsCategory) && newsCategory?.map((category) => (
                  <div key={category?.id} className="col-3">
                    <div className="info-box flex justify-content-between h-full">
                      <div>
                        <div className="flex align-items-start justify-content-between">
                          <div className="pr-0">
                            <h2>{category?.name}</h2>
                          </div>
                          <div className="card">
                            <PR.Menu
                              model={items}
                              popup
                              ref={menuRight}
                              id="popup_menu_right"
                              className="export-popup"
                              popupAlignment="right"
                            />
                            <PR.Button
                              icon="pi pi-ellipsis-v"
                              className="export-button"
                              onClick={(event) => {
                                setSelectedCategory(category);
                                menuRight.current.toggle(event);
                              }}
                              aria-controls="popup_menu_right"
                              aria-haspopup
                            />
                          </div>
                        </div>
                        <p dangerouslySetInnerHTML={{ __html: dompurifyText(category?.description) }}></p>
                      </div>
                      <h4>
                        <i className="pi pi-book"></i> {category?.newsUpdatesCount >= 0 ? category?.newsUpdatesCount : 0}{" "} news items
                      </h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          }

        </section>
        {/* Create/Edit Category Dialog */}
        <PR.Dialog
          visible={visible}
          footer={footerContent}
          onHide={() => {
            setVisible(false);
            setIsEdit(false);
            setSelectedCategory(null);
          }}
          className="logout-dialog response-dialog tags-dialog"
        >
          <Formik
            initialValues={isEdit && selectedCategory ? { name: selectedCategory.name, description: decodeHtml(selectedCategory.description) } : { name: "", description: "" }}
            validationSchema={validationSchema}
            
            onSubmit={(values, { setSubmitting }) =>
              handleCreateCategory(values, setSubmitting)
            }
          >
            {({ isSubmitting, dirty }) => { return (
              <Form id="categoryForm">
                <div className="dialog-header">
                  <div className="mb-5">
                    <h2>{isEdit ? "Edit Category" : "Create Category"}</h2>
                    <p>{isEdit ? "Edit the category details" : "Categorize the alert according to the relevant tag"}</p>
                  </div>
                </div>
                <div className="dialog-body-section">
                  <div className="flex flex-column form-input gap-2 mb-3">
                    <label htmlFor="name">
                      Category name <span style={{ color: "red" }}>*</span>
                    </label>
                    <Field
                      name="name"
                      as={PR.InputText}
                      id="name"
                      type="text"
                      placeholder="Enter category name"
                      aria-describedby="name"
                      style={{ color: 'black' }}
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="error-message"
                    />
                  </div>
                  <div className="flex flex-column gap-2 mb-3">
                    <label htmlFor="description">
                      Category description{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                      <Field
                        name="description"
                        as={PR.InputTextarea}
                        placeholder="Something about category.."
                        className="description-textarea"
                        style={{ color: 'black' }}
                      />
                      <ErrorMessage
                        name="description"
                        component="div"
                        className="error-message"
                      />
                  </div>
                </div>
                {(!dirty && isEdit) && <div style={{
                  height: '36px',
                  position: 'absolute',
                  bottom: '15px',
                  width: '137px',
                  right: '27px',
                  zIndex: 9,
                  opacity: 0.5,
                  background: 'white'
                }}>
                </div>}
              </Form>
            )}}
          </Formik>
        </PR.Dialog>

        {/*  News Category delete dialog */}
        <PR.Dialog visible={showDeleteDialog} className="news-dialog" header="Delete news category" modal closable={false}
          footer={
            <>
              <PR.Button
                label="Cancel"
                className="p-button-text custom-cancel-button"
                onClick={() => setShowDeleteDialog(false)}
              />
              <PR.Button
                label="Delete"
                className="p-button-danger custom-delete-button"
                onClick={handleDeleteCategory}
              />
            </>
          }
        >
          <div className="confirmation-content">
            <span>Are you sure you want to delete this news category?</span>
          </div>
        </PR.Dialog>

      </Layout>
    </>
  );
};

export default NewsCategories;
